import { useEffect, useEffectEvent } from "react";

import { useIndustrySuggestionsStore } from "../store/useIndustrySuggestionsStore";

type UseSuggestionsImpressionParams = {
    /** Identifies what is on screen; a new signature is a new impression. */
    readonly signature: string;
    readonly isShowing: boolean;
    readonly isExpanded: boolean;
    readonly onImpression: () => void;
};

/**
 * Reports a suggestions row once per signature for as long as any instance of its card stays mounted,
 * so the panel's double-mounted card counts once and the next page view counts afresh.
 */
export const useSuggestionsImpression = ({
    signature,
    isShowing,
    isExpanded,
    onImpression,
}: UseSuggestionsImpressionParams): void => {
    const registerCard = useIndustrySuggestionsStore((state) => state.registerCard);
    const unregisterCard = useIndustrySuggestionsStore((state) => state.unregisterCard);
    const claimImpression = useIndustrySuggestionsStore((state) => state.claimImpression);
    const releaseImpression = useIndustrySuggestionsStore((state) => state.releaseImpression);

    // Fires on the row appearing, not on every change to `onImpression` while it stays up.
    const reportImpression = useEffectEvent(onImpression);

    useEffect(() => {
        registerCard();

        return unregisterCard;
    }, [registerCard, unregisterCard]);

    useEffect(() => {
        if (isShowing && claimImpression(signature)) {
            reportImpression();
        }
    }, [isShowing, signature, claimImpression]);

    useEffect(() => {
        if (!isExpanded) {
            releaseImpression();
        }
    }, [isExpanded, releaseImpression]);
};
